import { useMemo } from "react";
import { useNewTrialState } from "../legacy";
import useSeedCatalog, { type SeedCropDetails } from "./useSeedCatalog";
import { convertSeedRate } from "../services/seedAssignments";
import {
  hydrateStripSeedAssignments,
  legacyStripOwnerId,
  STRIP_STANDARD_OWNER,
  type StripSeedTrial,
} from "../services/stripSeedAssignments";
import {
  type SeedProduct,
  type SeedStripe,
  type StripeAssignments,
} from "../utils/seedFlow";

export const FIELD_STANDARD_PRODUCT_ID = STRIP_STANDARD_OWNER;

interface DensitySettings {
  density_range?: {
    min?: number;
    max?: number;
  } | null;
  density_options?: number[] | null;
}

interface ProtocolSeedProduct extends DensitySettings {
  id: string;
  variety: string;
  measure_unit?: string;
}

interface SeedProtocol {
  crop_details?: SeedCropDetails;
  product_details?: {
    seeds?: DensitySettings & {
      global_density_range?: boolean;
      measure_unit?: string;
      products?: ProtocolSeedProduct[];
    };
  };
}

interface ProductResult {
  products: SeedProduct[];
  error: string | null;
}

interface HydrationResult {
  assignments: StripeAssignments;
  error: string | null;
}

interface StripeSeedData {
  ready: boolean;
  hydrationError: string | null;
  unit: string;
  products: SeedProduct[];
  standardVarieties: SeedProduct[];
  initialStandardProduct?: string;
  initialStandardRate: number | null;
  initialAssignments: StripeAssignments;
  standardAssignmentProduct?: SeedProduct;
}

const PRODUCT_COLORS = ["#0074d4", "#fa5738", "#8650df", "#14803c"];

const nonEmpty = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();

  if (trimmed === undefined || trimmed === "") return undefined;

  return trimmed;
};

const useStripeSeedData = (
  stripes: SeedStripe[],
  savedTrial?: StripSeedTrial,
  isFetching = false
): StripeSeedData => {
  const { trialInfo, trialProtocolData } = useNewTrialState();
  const protocol = trialProtocolData as unknown as SeedProtocol | null;
  const seeds = protocol?.product_details?.seeds;
  const standard = savedTrial?.standard_field_properties;

  const catalog = useSeedCatalog(
    savedTrial?.protocol_id ?? trialInfo?.protocol_id,
    protocol?.crop_details
  );

  const unit = nonEmpty(seeds?.measure_unit) ?? "seeds/ha";

  const productResult = useMemo<ProductResult>(() => {
    try {
      const products: SeedProduct[] = (seeds?.products ?? []).map(
        (product, index) => {
          const global = seeds?.global_density_range === true;
          const settings = global ? seeds : product;
          const sourceUnit = global
            ? nonEmpty(seeds?.measure_unit) ?? unit
            : nonEmpty(product.measure_unit) ??
              nonEmpty(seeds?.measure_unit) ??
              unit;

          const min = settings?.density_range?.min;
          const max = settings?.density_range?.max;

          return {
            id: product.id,
            name: product.variety,
            color: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
            min:
              typeof min === "number"
                ? convertSeedRate(min, sourceUnit, unit)
                : 0,
            max:
              typeof max === "number"
                ? convertSeedRate(max, sourceUnit, unit)
                : Number.POSITIVE_INFINITY,
            densityOptions: (settings?.density_options ?? []).map((rate) =>
              convertSeedRate(rate, sourceUnit, unit)
            ),
          };
        }
      );

      const saved =
        savedTrial?.plot_property_assignments?.seed_product_assignments ?? [];

      saved.forEach((product) => {
        if (product.is_standard) return;

        const id =
          product.product_id ??
          legacyStripOwnerId(product.variety, product.measure_unit);

        if (!products.some((item) => item.id === id)) {
          products.push({
            id,
            name: product.variety,
            color: product.assignments[0]?.color ?? PRODUCT_COLORS[0],
            min: 0,
            max: Number.POSITIVE_INFINITY,
          });
        }
      });

      return { products, error: null };
    } catch (error) {
      return {
        products: [],
        error: error instanceof Error ? error.message : "Invalid seed units.",
      };
    }
  }, [seeds, savedTrial, unit]);

  const standardVarieties = useMemo<SeedProduct[]>(() => {
    const varieties: SeedProduct[] = (catalog.data ?? []).map((product) => ({
      id: product.id,
      name: product.commercial_name,
      color: "#b99af3",
      min: 0,
      max: Number.POSITIVE_INFINITY,
    }));

    const add = (id?: string | null, name?: string): void => {
      if (
        id === undefined ||
        id === null ||
        id.trim() === "" ||
        varieties.some((product) => product.id === id)
      ) {
        return;
      }

      varieties.push({
        id,
        name: name ?? id,
        color: "#b99af3",
        min: 0,
        max: Number.POSITIVE_INFINITY,
      });
    };

    add(standard?.standard_seed_product_id, standard?.standard_product);

    (savedTrial?.plot_property_assignments?.seed_product_assignments ?? [])
      .filter((product) => product.is_standard)
      .forEach((product) => {
        add(product.product_id, product.variety);
      });

    return varieties.sort((a, b) => a.name.localeCompare(b.name));
  }, [catalog.data, standard, savedTrial]);

  const initialStandardProduct =
    nonEmpty(standard?.standard_seed_product_id) ??
    standardVarieties.find(
      (product) => product.name === standard?.standard_product
    )?.id;

  const initialStandardRate =
    typeof standard?.standard_seed_rate === "number" &&
    Number.isFinite(standard.standard_seed_rate)
      ? standard.standard_seed_rate
      : null;

  const savedStandard = (
    savedTrial?.plot_property_assignments?.seed_product_assignments ?? []
  ).find((product) => product.is_standard);

  const standardAssignmentProduct: SeedProduct | undefined =
    savedStandard === undefined
      ? undefined
      : {
          id: savedStandard.product_id ?? "",
          name: savedStandard.variety,
          color: savedStandard.assignments[0]?.color ?? "#b99af3",
          min: 0,
          max: Number.POSITIVE_INFINITY,
        };

  const hydration = useMemo<HydrationResult>(() => {
    if (savedTrial === undefined) {
      return { assignments: {}, error: null };
    }

    try {
      return {
        assignments: hydrateStripSeedAssignments(savedTrial, stripes, unit),
        error: null,
      };
    } catch (error) {
      return {
        assignments: {},
        error:
          error instanceof Error
            ? error.message
            : "Could not restore seed assignments.",
      };
    }
  }, [savedTrial, stripes, unit]);

  return {
    ready:
      savedTrial !== undefined &&
      !isFetching &&
      protocol !== null &&
      protocol !== undefined &&
      productResult.error === null &&
      hydration.error === null,
    hydrationError: productResult.error ?? hydration.error,
    unit,
    products: productResult.products,
    standardVarieties,
    initialStandardProduct,
    initialStandardRate,
    initialAssignments: hydration.assignments,
    standardAssignmentProduct,
  };
};

export default useStripeSeedData;
